import { useEffect, useState, type FormEvent } from "react";
import AdminHeader from "../../components/AdminHeader";
import { FullPageLoading, InlineSpinner } from "../../components/Loading";
import { Eyebrow, HeadingItalic } from "../../components/design";
import {
  adminListCodes,
  adminCreateCode,
  adminUpdateCode,
  adminDeleteCode,
  type CodeAccount,
} from "../../lib/api";

export default function AdminCodes() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [codes, setCodes] = useState<CodeAccount[]>([]);

  // Create form
  const [newCode, setNewCode] = useState("");
  const [newDiscount, setNewDiscount] = useState("100");
  const [newMaxUses, setNewMaxUses] = useState("0");
  const [newNote, setNewNote] = useState("");
  const [newExpires, setNewExpires] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [busyCode, setBusyCode] = useState<string | null>(null);

  async function reload() {
    setLoading(true);
    try {
      const r = await adminListCodes();
      setCodes(r.codes || []);
      setError(null);
    } catch (e: any) {
      setError(e?.message || "Fehler beim Laden.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
  }, []);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setCreateError(null);
    const code = newCode.trim().toUpperCase();
    if (!code) {
      setCreateError("Bitte einen Code angeben.");
      return;
    }
    const discount = parseInt(newDiscount, 10);
    const maxUses = parseInt(newMaxUses, 10);
    if (isNaN(discount) || discount < 0 || discount > 100) {
      setCreateError("Rabatt muss 0–100 sein.");
      return;
    }
    if (isNaN(maxUses) || maxUses < 0) {
      setCreateError("Max. Einlösungen darf nicht negativ sein.");
      return;
    }
    setCreating(true);
    try {
      await adminCreateCode({
        code,
        discount_percent: discount,
        max_uses: maxUses,
        note: newNote.trim() || undefined,
        expires_at: newExpires.trim() || undefined,
      });
      setNewCode("");
      setNewDiscount("100");
      setNewMaxUses("0");
      setNewNote("");
      setNewExpires("");
      await reload();
    } catch (e: any) {
      setCreateError(e?.message || "Konnte Code nicht anlegen.");
    } finally {
      setCreating(false);
    }
  }

  async function onToggleActive(c: CodeAccount) {
    setBusyCode(c.code);
    try {
      await adminUpdateCode(c.code, { active: !c.active });
      await reload();
    } catch (e: any) {
      alert(e?.message || "Konnte Code nicht ändern.");
    } finally {
      setBusyCode(null);
    }
  }

  async function onDelete(c: CodeAccount) {
    const ok = window.confirm(
      `Code ${c.code} wirklich löschen?\n\nBisher eingelöst: ${c.uses_count}× — die zugehörigen Anmeldungen bleiben bestehen.`,
    );
    if (!ok) return;
    setBusyCode(c.code);
    try {
      await adminDeleteCode(c.code);
      await reload();
    } catch (e: any) {
      alert(e?.message || "Konnte Code nicht löschen.");
    } finally {
      setBusyCode(null);
    }
  }

  if (loading) return <FullPageLoading label="Lade Codes …" />;

  return (
    <div className="paper-bg" style={{ minHeight: "100vh" }}>
      <AdminHeader />
      <main
        className="desk-container-admin"
        style={{ padding: "36px 22px 60px" }}
      >
        <Eyebrow>Aktionscodes</Eyebrow>
        <HeadingItalic size={34}>
          Rabatte und <em>Friends & Family</em>.
        </HeadingItalic>
        <p
          style={{
            fontFamily: "var(--f-body)",
            fontSize: 15,
            color: "var(--ink-soft)",
            margin: "8px 0 28px",
            maxWidth: 640,
          }}
        >
          Jeder Code hat einen Prozentsatz Rabatt und ein optionales Limit für
          die Anzahl der Einlösungen. Codes aus dem <code>FRIENDS_FAMILY_CODES</code>{" "}
          Deploy-Secret erscheinen hier zusätzlich als Bootstrap-Einträge (100 %,
          unbegrenzt). Diese können nicht via UI bearbeitet werden.
        </p>

        {error && (
          <p
            role="alert"
            style={{
              padding: "12px 16px",
              background: "rgba(107,32,24,0.06)",
              border: "1px solid rgba(107,32,24,0.25)",
              borderRadius: 8,
              color: "var(--oxblood)",
              fontFamily: "var(--f-body)",
              fontStyle: "italic",
              fontSize: 14,
              marginBottom: 24,
            }}
          >
            {error}
          </p>
        )}

        {/* Create form */}
        <section
          className="card"
          style={{ padding: "20px 22px", marginBottom: 32 }}
        >
          <h2
            className="t-display-it"
            style={{ margin: "0 0 12px", fontSize: 20 }}
          >
            Neuen Code anlegen
          </h2>
          <form
            onSubmit={onCreate}
            style={{
              display: "grid",
              gap: 12,
              gridTemplateColumns: "1fr",
            }}
          >
            <div style={{ display: "grid", gap: 12, gridTemplateColumns: "2fr 1fr 1fr" }}>
              <div>
                <label className="t-caps" htmlFor="c-code" style={{ display: "block", marginBottom: 6 }}>
                  Code
                </label>
                <input
                  id="c-code"
                  type="text"
                  className="input"
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                  placeholder="WEIHNACHTEN-2026"
                  required
                  style={{ textTransform: "uppercase" }}
                />
              </div>
              <div>
                <label className="t-caps" htmlFor="c-discount" style={{ display: "block", marginBottom: 6 }}>
                  Rabatt %
                </label>
                <input
                  id="c-discount"
                  type="number"
                  min={0}
                  max={100}
                  className="input"
                  value={newDiscount}
                  onChange={(e) => setNewDiscount(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="t-caps" htmlFor="c-max" style={{ display: "block", marginBottom: 6 }}>
                  Max. Einlösungen
                </label>
                <input
                  id="c-max"
                  type="number"
                  min={0}
                  className="input"
                  value={newMaxUses}
                  onChange={(e) => setNewMaxUses(e.target.value)}
                  placeholder="0 = unbegrenzt"
                  required
                />
              </div>
            </div>
            <div style={{ display: "grid", gap: 12, gridTemplateColumns: "2fr 1fr" }}>
              <div>
                <label className="t-caps" htmlFor="c-note" style={{ display: "block", marginBottom: 6 }}>
                  Notiz (optional)
                </label>
                <input
                  id="c-note"
                  type="text"
                  className="input"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="z. B. Weihnachts-Aktion, Pressekontakt"
                />
              </div>
              <div>
                <label className="t-caps" htmlFor="c-exp" style={{ display: "block", marginBottom: 6 }}>
                  Ablauf (optional)
                </label>
                <input
                  id="c-exp"
                  type="date"
                  className="input"
                  value={newExpires}
                  onChange={(e) => setNewExpires(e.target.value)}
                />
              </div>
            </div>
            {createError && (
              <p
                role="alert"
                style={{
                  fontFamily: "var(--f-body)",
                  fontStyle: "italic",
                  fontSize: 13,
                  color: "var(--oxblood)",
                  margin: 0,
                }}
              >
                {createError}
              </p>
            )}
            <div>
              <button
                type="submit"
                disabled={creating}
                className="btn-primary"
                style={{ padding: "12px 22px", minHeight: 0, fontSize: 14 }}
              >
                {creating ? <InlineSpinner /> : "Code anlegen"}
              </button>
            </div>
          </form>
        </section>

        {/* List */}
        <section className="card" style={{ padding: 0, overflow: "hidden" }}>
          <div
            style={{
              padding: "16px 22px",
              borderBottom: "1px solid rgba(43,26,15,0.10)",
            }}
          >
            <h2 className="t-display-it" style={{ margin: 0, fontSize: 20 }}>
              Alle Codes ({codes.length})
            </h2>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontFamily: "var(--f-body)",
              }}
            >
              <thead>
                <tr style={{ background: "var(--paper-200)" }}>
                  <Th>Code</Th>
                  <Th align="right">Rabatt</Th>
                  <Th align="right">Einlösungen</Th>
                  <Th>Status</Th>
                  <Th>Ablauf</Th>
                  <Th>Notiz</Th>
                  <Th style={{ textAlign: "right" }}>Aktion</Th>
                </tr>
              </thead>
              <tbody>
                {codes.map((c) => {
                  const isEnv = c.source === "env";
                  const exhausted =
                    c.max_uses > 0 && c.uses_count >= c.max_uses;
                  return (
                    <tr
                      key={c.code}
                      style={{
                        borderBottom: "1px solid rgba(43,26,15,0.06)",
                      }}
                    >
                      <Td>
                        <span
                          style={{
                            fontFamily: "var(--f-display)",
                            fontStyle: "italic",
                            fontSize: 17,
                            color: "var(--ink)",
                          }}
                        >
                          {c.code}
                        </span>
                      </Td>
                      <Td align="right">
                        <span style={{ color: "var(--ink-soft)" }}>
                          {c.discount_percent} %
                        </span>
                      </Td>
                      <Td align="right">
                        <span
                          style={{
                            color: exhausted ? "var(--oxblood)" : "var(--ink-soft)",
                            fontWeight: exhausted ? 600 : 400,
                          }}
                        >
                          {c.uses_count} / {c.max_uses === 0 ? "∞" : c.max_uses}
                        </span>
                      </Td>
                      <Td>
                        {isEnv ? (
                          <SourceBadge variant="env" label="Bootstrap" />
                        ) : !c.active ? (
                          <SourceBadge variant="off" label="Deaktiviert" />
                        ) : exhausted ? (
                          <SourceBadge variant="warn" label="Aufgebraucht" />
                        ) : (
                          <SourceBadge variant="on" label="Aktiv" />
                        )}
                      </Td>
                      <Td>
                        {c.expires_at ? (
                          <span style={{ fontSize: 13, color: "var(--ink-soft)" }}>
                            {formatDate(c.expires_at)}
                          </span>
                        ) : (
                          <span style={{ color: "var(--ink-faint)" }}>—</span>
                        )}
                      </Td>
                      <Td>
                        {c.note ? (
                          <span style={{ fontSize: 13, color: "var(--ink-soft)" }}>
                            {c.note}
                          </span>
                        ) : (
                          <span style={{ color: "var(--ink-faint)" }}>—</span>
                        )}
                      </Td>
                      <Td style={{ textAlign: "right" }}>
                        {isEnv ? (
                          <span className="t-caps" style={{ color: "var(--ink-faint)" }}>
                            geschützt
                          </span>
                        ) : (
                          <div style={{ display: "inline-flex", gap: 6 }}>
                            <button
                              onClick={() => onToggleActive(c)}
                              disabled={busyCode === c.code}
                              className="btn-secondary"
                              style={{
                                padding: "5px 10px",
                                minHeight: 0,
                                fontSize: 12,
                              }}
                            >
                              {busyCode === c.code ? (
                                <InlineSpinner />
                              ) : c.active ? (
                                "Deaktivieren"
                              ) : (
                                "Aktivieren"
                              )}
                            </button>
                            <button
                              onClick={() => onDelete(c)}
                              disabled={busyCode === c.code}
                              className="btn-secondary"
                              style={{
                                padding: "5px 10px",
                                minHeight: 0,
                                fontSize: 12,
                                color: "var(--oxblood)",
                                borderColor: "var(--oxblood)",
                              }}
                            >
                              Löschen
                            </button>
                          </div>
                        )}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}

function Th({
  children,
  style,
  align = "left",
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
  align?: "left" | "right";
}) {
  return (
    <th
      className="t-caps"
      style={{
        textAlign: align,
        padding: "10px 16px",
        fontWeight: 600,
        color: "var(--ink-faint)",
        ...style,
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  style,
  align = "left",
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
  align?: "left" | "right";
}) {
  return (
    <td
      style={{
        textAlign: align,
        padding: "12px 16px",
        verticalAlign: "middle",
        ...style,
      }}
    >
      {children}
    </td>
  );
}

function SourceBadge({
  variant,
  label,
}: {
  variant: "env" | "on" | "off" | "warn";
  label: string;
}) {
  const palette: Record<typeof variant, { bg: string; fg: string }> = {
    env: { bg: "rgba(168,122,58,0.18)", fg: "var(--ochre)" },
    on: { bg: "rgba(95,107,79,0.18)", fg: "var(--sage)" },
    off: { bg: "rgba(43,26,15,0.10)", fg: "var(--ink-faint)" },
    warn: { bg: "rgba(107,32,24,0.10)", fg: "var(--oxblood)" },
  } as const;
  const c = palette[variant];
  return (
    <span
      className="t-caps"
      style={{
        display: "inline-block",
        padding: "3px 8px",
        background: c.bg,
        color: c.fg,
        borderRadius: 999,
      }}
    >
      {label}
    </span>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}
